import { db } from '@/firebase.js'
import { firestoreAction } from 'vuexfire'

const getDefaultState = () => {
  return {
    data: {},
    ref: null
  }
}

const state = getDefaultState()

const getters = {
  ref(state) {
    return state.ref
  },
  data(state) {
    try {
      return state.data.reduce((acc, doc) => {
        acc[doc.id] = doc
        return acc
      }, {})
    } catch {
      return {}
    }
  },
  products(state) {
    try {
      const doc = state.data.find((doc) => doc.id === 'products')
      return doc ? { ...doc } : null
    } catch {
      return null
    }
  },
  productById(state, getters) {
    return (id) => {
      return getters.products[id] || null
    }
  },
  productName(state, getters) {
    return (id) => {
      try {
        return getters.products[id].name
      } catch {
        return null
      }
    }
  },
  productYieldPercent(state, getters) {
    return (id) => {
      try {
        return getters.products[id].yieldPercent
      } catch {
        return null
      }
    }
  },
  productUnitRecipe(state, getters) {
    return (id) => {
      try {
        return getters.products[id].unitRecipe
      } catch {
        return null
      }
    }
  },
  productRecipeUnitPerOrderUnitById(state, getters) {
    return (id) => {
      try {
        return getters.products[id].recipeUnitPerOrderUnit
      } catch {
        return null
      }
    }
  },
  isProduct(state, getters) {
    return (id) => {
      try {
        return Object.keys(getters.products).some(
          (k) => getters.products[k].id === id
        )
      } catch {
        return false
      }
    }
  },
  storeProductIdsByGenericIds(state, getters, rootState, rootGetters) {
    try {
      return Object.fromEntries(
        Object.entries(
          rootGetters['storeModule/products/productIdsByGenericIds']
        ).filter(([key]) => getters.isProduct(key))
      )
    } catch {
      return {}
    }
  },
  storeProductIdByGenericId(state, getters) {
    return (id) => {
      return getters.storeProductIdsByGenericIds[id] || null
    }
  },
  genericProductIdsWithoutLocalIds(state, getters, rootState, rootGetters) {
    try {
      const productIdMappings =
        rootGetters['storeModule/products/productIdsByGenericIds']
      return Object.keys(getters.products).filter(
        (genericId) => !productIdMappings[genericId]
      )
    } catch {
      return []
    }
  },
  genericProductNamesWithoutLocalProduct(
    state,
    getters,
    rootState,
    rootGetters
  ) {
    try {
      const productIdMappings =
        rootGetters['storeModule/products/productIdsByGenericIds']
      return Object.keys(getters.products)
        .filter((genericId) => !productIdMappings[genericId])
        .map(
          (genericId) =>
            getters.products[genericId] && getters.products[genericId].name
        )
        .filter((name) => name) // Filter out undefined or null names
    } catch {
      return []
    }
  },
  recipes(state, getters, rootState, rootGetters) {
    // Use the dedicated recipes module instead of duplicating functionality
    return rootGetters['recipes/recipes'] || {}
  },
  recipeById(state, getters, rootState, rootGetters) {
    return (id) => {
      // Use the dedicated recipes module
      return rootGetters['recipes/recipeById'](id)
    }
  },
  recipeName(state, getters, rootState, rootGetters) {
    return (id) => {
      // Use the dedicated recipes module
      return rootGetters['recipes/recipeName'](id)
    }
  },
  isRecipe(state, getters, rootState, rootGetters) {
    return (id) => {
      // Use the dedicated recipes module
      return rootGetters['recipes/isRecipe'](id)
    }
  },
  global(state) {
    try {
      const doc = state.data.find((doc) => doc.id === 'global')
      return doc ? { ...doc } : null
    } catch {
      return null
    }
  },
  stores(state) {
    try {
      const doc = state.data.find((doc) => doc.id === 'global')
      return doc ? doc.stores || {} : {}
    } catch {
      return {}
    }
  },
  storeIds(state, getters) {
    return Object.keys(getters.stores)
  },
  storeNameById(state, getters) {
    return (id) => {
      try {
        return getters.stores[id] || null
      } catch {
        return null
      }
    }
  },
  genericProductById(state, getters) {
    return (id) => {
      return getters.products[id] || null
    }
  },
  genericProductName(state, getters) {
    return (id) => {
      try {
        return getters.products[id].name
      } catch {
        return null
      }
    }
  },
  resources(state, getters, rootState, rootGetters) {
    try {
      const doc = state.data.find((doc) => doc.id === 'resources')
      if (!doc) return null

      const storeCode = rootGetters['storeModule/storeCode']
      if (!storeCode) return doc

      return Object.fromEntries(
        Object.entries(doc).filter(([, resource]) => {
          return (
            resource.active &&
            (resource.locations.length === 0 ||
              resource.locations.includes(storeCode))
          )
        })
      )
    } catch {
      return null
    }
  },
  tasks(state, getters, rootState, rootGetters) {
    try {
      const doc = state.data.find((doc) => doc.id === 'tasks')
      if (!doc) return null

      const storeCode = rootGetters['storeModule/storeCode']
      if (!storeCode) return doc

      return Object.fromEntries(
        Object.entries(doc).filter(([, task]) => {
          return (
            task.active !== false &&
            (task.locations.length === 0 || task.locations.includes(storeCode))
          )
        })
      )
    } catch {
      return null
    }
  },
  taskById(state, getters) {
    return (id) => {
      try {
        return getters.tasks[id] || null
      } catch {
        return null
      }
    }
  },
  weeklyTasks(state, getters) {
    try {
      return Object.fromEntries(
        Object.entries(getters.tasks).filter(
          ([, task]) => task.frequency === 'weekly'
        )
      )
    } catch {
      return {}
    }
  },
  monthlyTasks(state, getters) {
    try {
      return Object.fromEntries(
        Object.entries(getters.tasks).filter(
          ([, task]) => task.frequency === 'monthly'
        )
      )
    } catch {
      return {}
    }
  },
  storeInfoById: (state) => (storeId) => {
    try {
      const doc = state.data.find((doc) => doc.id === 'global')
      return doc ? doc.stores[storeId] || {} : {}
    } catch {
      return {}
    }
  },
  storeRegionById: (state, getters) => (storeId) => {
    try {
      // First check if we have regions data at the top level
      const regions = state.data.find((doc) => doc.id === 'regions')

      if (regions && regions.id !== 'all') {
        // Look through all regions to find which one contains this store
        for (const regionId in regions) {
          if (
            regions[regionId].stores &&
            regions[regionId].stores.includes(storeId)
          ) {
            return regions[regionId].id || regionId
          }
        }
      }

      // Fallback to check in the old location (global.regions)
      // const globalDoc = state.data.find((doc) => doc.id === 'global')
      // if (globalDoc && globalDoc.regions) {
      //   for (const regionId in globalDoc.regions) {
      //     if (
      //       globalDoc.regions[regionId].stores &&
      //       globalDoc.regions[regionId].stores.includes(storeId)
      //     ) {
      //       return globalDoc.regions[regionId].id || regionId
      //     }
      //   }
      // }

      return ''
    } catch {
      return ''
    }
  },
  regions(state) {
    try {
      // First check if we have regions data at the top level
      const regionsDoc = state.data.find((doc) => doc.id === 'regions')
      if (regionsDoc) {
        return regionsDoc
      }

      // Fallback to check in the old location (global.regions)
      // const globalDoc = state.data.find((doc) => doc.id === 'global')
      // return globalDoc && globalDoc.regions ? globalDoc.regions : {}
    } catch {
      return {}
    }
  },
  regionById: (state, getters) => (regionId) => {
    try {
      const regions = getters.regions
      return regions[regionId] || null
    } catch {
      return null
    }
  },
  regionTimezoneById: (state, getters) => (regionId) => {
    try {
      const region = getters.regionById(regionId)
      return region ? region.timeZone : 'America/New_York' // Default fallback
    } catch {
      return 'America/New_York' // Default fallback
    }
  },
  storesByRegionId: (state, getters) => (regionId) => {
    try {
      const region = getters.regionById(regionId)
      return region && region.stores ? region.stores : []
    } catch {
      return []
    }
  },
  settings(state) {
    try {
      const doc = state.data.find((doc) => doc.id === 'settings')
      return doc ? { ...doc } : null
    } catch {
      return null
    }
  },
  settingsStoreIds(state, getters) {
    try {
      return getters.settings.storeIds || {}
    } catch {
      return {}
    }
  },
  settingsShifts(state, getters) {
    try {
      return getters.settings.shifts || []
    } catch {
      return []
    }
  },
  settingsHours(state, getters) {
    try {
      return getters.settings.hours || []
    } catch {
      return []
    }
  },
  settingsProductLocations(state, getters) {
    try {
      return getters.settings.productLocations || []
    } catch {
      return []
    }
  },
  settingsProductExpenseCategories(state, getters) {
    try {
      return getters.settings.productExpenseCategories || []
    } catch {
      return []
    }
  },
  settingsPositions(state, getters) {
    try {
      return getters.settings.positions || []
    } catch {
      return []
    }
  },
  settingsInvoiceActions(state, getters) {
    try {
      return getters.settings.invoiceActions || []
    } catch {
      return []
    }
  },
  settingsTaskFrequencies(state, getters) {
    try {
      return getters.settings.taskFrequencies || []
    } catch {
      return []
    }
  }
}

const actions = {
  bindStore: firestoreAction(({ bindFirestoreRef, commit, state }) => {
    commit('setRef', db.collection('config'))
    return bindFirestoreRef('data', state.ref)
  }),
  unbindStore: firestoreAction(({ unbindFirestoreRef }) => {
    return unbindFirestoreRef('data')
  }),
  updateSettings({ getters }, data) {
    return getters.ref.doc('settings').update(data)
  }
}

const mutations = {
  setRef(state, ref) {
    state.ref = ref
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  }
}

const global = {
  namespaced: true,

  state,
  getters,
  mutations,
  actions
}

export default global
